import { datadogRum, type RumEvent } from '@datadog/browser-rum'
import type { Context } from '@datadog/browser-core'
import {
  datadogLogs,
  type LogsEvent,
  type HandlerType,
  type StatusType as LogLevel,
} from '@datadog/browser-logs'
import { Logger, type LogFunction } from './logger'
import { browserLogsConfig, globalContextConfig, rumConfig, UserInfo } from './utils'
import Config from 'APP/Config/index'
import { appInfo } from 'APP/Lib/AppInfo'
import { ConfigInitError } from 'APP/Config/types'

interface WebNavigator {
  userAgent: string
  language: string
  languages: readonly string[]
  platform: string
  cookieEnabled: boolean
  onLine: boolean
  doNotTrack: string | null
  hardwareConcurrency: number
  maxTouchPoints: number
  connection?: {
    effectiveType?: string
    downlink?: number
    rtt?: number
    saveData?: boolean
  }
  deviceMemory?: number
}

interface WebPerformance {
  memory?: {
    totalJSHeapSize?: number
    usedJSHeapSize?: number
    jsHeapSizeLimit?: number
  }
  timing?: {
    navigationStart: number
    loadEventEnd: number
    domContentLoadedEventEnd: number
  }
}

interface WebWindow extends Window {}

export const configureLoggerForEnv = (env: string) => {
  const isDev = env.toLowerCase().startsWith('dev-')

  let logLevel: LogLevel = 'info'
  let logHandlers: HandlerType[] = ['http']

  if (isDev) {
    logLevel = 'debug'
    // Multiplex logs to Datadog + the console in dev
    logHandlers.push('console')
  }

  datadogLogs.logger.setLevel(logLevel)
  datadogLogs.logger.setHandler(logHandlers)
}

export const stripDataFromEvent = (event: RumEvent | LogsEvent): boolean => {
  // remove the hash from the URL to avoid sending potentially sensitive information to Datadog
  // at minimum, this hash includes the member's access token, which is PII.
  event.view.url = event.view.url.replace(/#[^]*/, '#<HASH_REDACTED>')

  return true
}

export const initDatadogRUM = async (configInitResult: ConfigInitError) => {
  // Temporarily setting session sample rate to 0
  // We will enable it later after gaining a better understanding of the missing masking issue on images.

  datadogRum.init({
    ...rumConfig,
    beforeSend: /* istanbul ignore next */ (event, _) => stripDataFromEvent(event),
  })

  datadogLogs.init({
    ...browserLogsConfig,
    beforeSend: /* istanbul ignore next */ (event, _) => stripDataFromEvent(event),
  })
  configureLoggerForEnv(browserLogsConfig.env)

  // Set global context variables
  setDdGlobalContext({ ...globalContextConfig, ...appInfo })
  // Dynamically update the environment tag in the global context
  // @ts-expect-error FIXME: onConfigKeyChange typing does not match usage
  Config.onConfigKeyChange('ENVIRONMENT', (done: () => void, updatedEnv: string) => {
    // TODO: We should stop the session and re-init the SDK here so it uses the proper site and env tag.
    // This isn't possible at the moment, so instead we add the `environment` context property.
    setDdGlobalContextProperty('environment', updatedEnv.toLowerCase())
    configureLoggerForEnv(updatedEnv)
    done()
  })

  // Send the config init error to Datadog if it exists
  if (configInitResult) {
    logger.error('Config initialization failed', {}, configInitResult)
  }
}

// Platform-specific implementations
export const trackDdEvent = (eventName: string, context?: object) => {
  datadogRum.addAction(eventName, context)
}

export const startDdView = (_viewKey: string, viewName: string, context?: object) => {
  datadogRum.startView(viewName)
  if (context) {
    datadogRum.setViewContext(context as Context)
  }
}

export const getWebContext = () => {
  try {
    const webNavigator = (globalThis as unknown as { navigator?: WebNavigator }).navigator
    const webWindow = typeof window !== 'undefined' ? (window as unknown as WebWindow) : undefined
    const webPerformance = (globalThis as unknown as { performance?: WebPerformance }).performance
    const memoryLimit = webPerformance?.memory?.jsHeapSizeLimit
    const usedMemory =
      typeof window !== 'undefined' ? webPerformance?.memory?.usedJSHeapSize : undefined

    return {
      // Browser information (never-changing facts — omitted rather than faked if unavailable)
      user_agent: webNavigator?.userAgent,
      browser_platform: webNavigator?.platform,

      // Hardware information
      hardware_concurrency: webNavigator?.hardwareConcurrency,
      max_touch_points: webNavigator?.maxTouchPoints,
      device_memory: webNavigator?.deviceMemory,

      // Security/location information
      is_secure_context: webWindow?.isSecureContext,
      protocol: webWindow?.location?.protocol,
      hostname: webWindow?.location?.hostname,

      // Page-load performance facts
      memory_limit: memoryLimit,
      performance_timing: {
        navigation_start: webPerformance?.timing?.navigationStart,
        load_event_end: webPerformance?.timing?.loadEventEnd,
        dom_content_loaded: webPerformance?.timing?.domContentLoadedEventEnd,
      },

      // Browser settings — user-configurable and can change mid-session without a reload
      browser_language: webNavigator ? webNavigator.language : 'unknown',
      browser_languages: webNavigator ? webNavigator.languages : 'unknown',
      browser_cookie_enabled: webNavigator ? webNavigator.cookieEnabled : 'unknown',
      browser_online: webNavigator ? webNavigator.onLine : 'unknown',
      browser_do_not_track: webNavigator ? webNavigator.doNotTrack : 'unknown',
      timezone:
        typeof Intl !== 'undefined' ? Intl.DateTimeFormat().resolvedOptions().timeZone : 'unknown',

      // Screen information
      screen_width: typeof window !== 'undefined' ? webWindow?.screen?.width : 'unknown',
      screen_height: typeof window !== 'undefined' ? webWindow?.screen?.height : 'unknown',
      screen_avail_width: typeof window !== 'undefined' ? webWindow?.screen?.availWidth : 'unknown',
      screen_avail_height:
        typeof window !== 'undefined' ? webWindow?.screen?.availHeight : 'unknown',
      screen_color_depth: typeof window !== 'undefined' ? webWindow?.screen?.colorDepth : 'unknown',
      screen_pixel_depth: typeof window !== 'undefined' ? webWindow?.screen?.pixelDepth : 'unknown',
      viewport_width: typeof window !== 'undefined' ? webWindow?.innerWidth : 'unknown',
      viewport_height: typeof window !== 'undefined' ? webWindow?.innerHeight : 'unknown',
      device_pixel_ratio: typeof window !== 'undefined' ? webWindow?.devicePixelRatio : 'unknown',

      // Network information
      connection_type: webNavigator
        ? webNavigator.connection?.effectiveType || 'unknown'
        : 'unknown',
      connection_downlink: webNavigator
        ? webNavigator.connection?.downlink || 'unknown'
        : 'unknown',
      connection_rtt: webNavigator ? webNavigator.connection?.rtt || 'unknown' : 'unknown',
      connection_save_data: webNavigator ? webNavigator.connection?.saveData || false : false,

      // Memory information
      memory_total:
        typeof window !== 'undefined'
          ? webPerformance?.memory?.totalJSHeapSize || 'unknown'
          : 'unknown',
      memory_used: usedMemory ?? 'unknown',
      memory_warning:
        memoryLimit !== undefined && usedMemory !== undefined
          ? usedMemory > memoryLimit * 0.8
          : false,

      // Embedded environment
      is_embedded: false,
    }
  } catch (_e) {
    return {}
  }
}

export const setDdUser = (props: UserInfo) => {
  datadogRum.setUser(props)
  datadogLogs.setUser(props)
}

export const setDdGlobalContext = (context: Record<string, any>) => {
  datadogRum.setGlobalContext(context)
  datadogLogs.setGlobalContext(context)
}

export const setDdGlobalContextProperty = (key: string, value: any) => {
  datadogRum.setGlobalContextProperty(key, value)
  datadogLogs.setGlobalContextProperty(key, value)
}

export class WebLogger extends Logger {
  createLogFunction = (level: LogLevel): LogFunction => {
    return (message, context = {}, error = undefined) => {
      if (error instanceof Error) {
        datadogLogs.logger[level](message, context, error)
      } else {
        datadogLogs.logger[level](message, { ...context, error })
      }
    }
  }

  debug = this.createLogFunction('debug')
  info = this.createLogFunction('info')
  warn = this.createLogFunction('warn')

  error: LogFunction = (message, context = {}, error = undefined) => {
    const mergedContext = { ...context, ...getWebContext() }
    if (error instanceof Error) {
      datadogLogs.logger.error(message, mergedContext, error)
    } else {
      datadogLogs.logger.error(message, { ...mergedContext, error })
    }
  }
}

export const logger: Logger = new WebLogger()
